/**
 * iOS: open the keyboard for a field that doesn't exist yet.
 *
 * iOS Safari only raises the keyboard when `focus()` runs synchronously inside a user
 * gesture. A field that mounts after the tap (a modal step, a remounted input) and is
 * focused from an effect/timeout gets the caret but NO keyboard — the passenger has to
 * tap it again. Android has no such rule.
 *
 * The workaround: during the tap, focus an invisible proxy input (keyboard opens), then
 * once the real field is mounted, move focus to it — iOS keeps the keyboard up across
 * that transfer — and drop the proxy.
 *
 *   onClick: primeIOSKeyboard();          // in the gesture
 *   effect:  focusFromProxy(id);          // after the field mounts
 *
 * No-op off iOS, so Android / desktop keep their plain focus().
 */

import {isIOS} from './platform.js';

const PROXY_ID = 'hopn-ios-focus-proxy';
// Safety net: never leave the proxy (and its keyboard) up if nothing claims it.
const PROXY_TTL_MS = 2000;
let ttlTimer = null;

export function releaseIOSKeyboardProxy() {
    clearTimeout(ttlTimer);
    document.getElementById(PROXY_ID)?.remove();
}

export function primeIOSKeyboard() {
    if (typeof document === 'undefined' || !isIOS()) return;
    releaseIOSKeyboardProxy();
    const proxy = document.createElement('input');
    proxy.id = PROXY_ID;
    proxy.type = 'text';
    proxy.setAttribute('aria-hidden', 'true');
    proxy.tabIndex = -1;
    // Visible to iOS (display:none / visibility:hidden can't take focus) but not to the
    // user. Pinned to the top so iOS has no reason to pan the page to reveal it, and
    // 16px so it doesn't trigger iOS's focus zoom.
    proxy.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;'
        + 'border:0;padding:0;font-size:16px;pointer-events:none;z-index:-1;';
    document.body.appendChild(proxy);
    proxy.focus();
    ttlTimer = setTimeout(() => {
        if (document.activeElement === proxy) proxy.blur();
        releaseIOSKeyboardProxy();
    }, PROXY_TTL_MS);
}

/**
 * Focus the real field (by id) and drop the proxy. Works on every platform. If the field
 * hasn't mounted yet (slow modal/step render) it retries briefly rather than dropping the
 * proxy early — that would close the keyboard it was holding open.
 */
export function focusFromProxy(target, {attempts = 20, focusOptions} = {}) {
    if (typeof document === 'undefined') return;
    // `target` is an element id, or a function returning the element (e.g. a ref).
    const el = typeof target === 'function' ? target() : document.getElementById(target);
    if (el) {
        el.focus(focusOptions);
        releaseIOSKeyboardProxy();
        return;
    }
    if (attempts > 0) setTimeout(() => focusFromProxy(target, {attempts: attempts - 1, focusOptions}), 50);
    else releaseIOSKeyboardProxy();
}
